<template>
  <div
    :class="`formulate-input-element formulate-input-element--${context.type} ${hasRequiredError}`"
    :data-type="context.type"
    @click="handleOpenModal"
  >
    <div
      class="mc-input d-inline-flex"
      :class="{
        'input-md': size == 'md',
        'input-sm': size == 'sm',
        __disabled: disabled,
        __multiple: hasSelected,
      }"
    >
      <!-- This input field is only used to show placeholder using regular input style -->
      <!-- If no value is selected -->
      <input
        v-if="!hasSelected"
        :placeholder="attrs.placeholder || context.label"
        style="border: 0; width: 200px"
        readonly
      />
      <div v-else-if="multiple" class="d-flex gap-2 flex-wrap">
        <template v-for="(value, index) in tagValue">
          <div
            v-if="index < 2"
            :key="index"
            v-b-tooltip.hover="value.length > 25 ? value : null"
            :class="value === attrs.disabledItem ? '__tag-disabled' : '__tag'"
          >
            <span>{{ value.length > 25 ? value.slice(0, 22) + '...' : value }}</span>
            <i
              v-if="value !== attrs.disabledItem && !isItemDisabled(value)"
              class="icon-outline_close"
              @click.stop="handleRemoveTag(value)"
            />
          </div>
          <div v-else-if="tagValue.length > 2 && index == 2" :key="index" class="__count">
            <i class="icon-fill_plus" />
            <span>{{
              attrs.hasDisabledItem ? context.model.length - 1 : context.model.length - 2
            }}</span>
          </div>
        </template>
      </div>
      <div v-else-if="localFullObject?.[displayedBy]">
        {{ localFullObject[displayedBy] }}
      </div>
      <div v-else>{{ context.model }}</div>

      <i class="icon-fill_menu d-flex align-items-center" />
    </div>

    <ModalValueHelp
      :context="context"
      :attrs="attrs"
      :multiple="multiple"
      :fullObject="localFullObject"
      @update:fullObject="onUpdate"
    >
      <template v-for="(val, key) in slotFields" #[val.name]="{ item }">
        <!-- eslint-disable-next-line vue/require-component-is -->
        <component :is="val.slotComponent" :key="key" :item="item" />
      </template>
    </ModalValueHelp>
  </div>
</template>

<script>
import ModalValueHelp from './components/ModalValueHelp'
import isEmpty from 'lodash/isEmpty'

export default {
  components: {
    ModalValueHelp,
  },
  props: {
    context: {
      type: Object,
      required: true,
    },

    size: {
      type: String,
      default: 'md',
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    multiple: {
      type: Boolean,
      default: false,
    },

    displayedBy: {
      type: String,
    },

    fullObject: {},

    canBeNull: {
      type: Boolean,
      default: false,
    },

    identifier: {
      type: String,
      default: 'id',
    },
  },
  emits: ['update:fullObject'],
  data() {
    return {
      localFullObject: {},
    }
  },
  computed: {
    attrs() {
      return this.context.attributes
    },

    slotFields() {
      return this.attrs?.tableFields ? this.attrs.tableFields.filter((v) => v.isSlot) : []
    },

    // add classes if it has validation error
    hasRequiredError() {
      if (this.context.visibleValidationErrors.includes('Wajib diisi'))
        return 'formulate-input-error__required'
      return ''
    },

    // if there is selected value(s)
    hasSelected() {
      if (this.attrs.hasDisabledItem) return true
      if (this.multiple) return this.context.model.length !== 0
      return this.context.model !== ''
    },

    tagValue() {
      let objects = this.localFullObject || this.context.model
      if (!Array.isArray(objects)) objects = [objects]
      const value = objects.map((v) => v[this.displayedBy])
      if (this.attrs.hasDisabledItem) {
        const disabledItem = this.attrs.disabledItem
        value.unshift(disabledItem)
      }
      return value.filter((v) => v !== null && v !== undefined && v !== '')
    },
  },
  watch: {
    fullObject: {
      handler(val) {
        this.localFullObject = this.multiple ? [...val] : { ...val }
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    isEmpty,
    onUpdate($event) {
      // Some orders have null customer id but still have customer name
      // So we need to handle when customer id is null but when user click apply button without selecting new customer, it won't be changed
      if (this.canBeNull && this.fullObject[this.identifier] === null && isEmpty($event)) {
        this.$emit('update:fullObject', this.fullObject)
        return
      }
      this.localFullObject = this.multiple ? [...$event] : { ...$event }
      this.$emit('update:fullObject', $event)
    },
    handleOpenModal() {
      if (this.disabled) return
      this.$bvModal.show(`${this.attrs.name}-modal-value-help`)
    },
    handleRemoveTag(value) {
      if (!this.multiple || !Array.isArray(this.localFullObject)) return

      let itemIndex = -1
      if (this.attrs.trackBy) {
        itemIndex = this.localFullObject.findIndex((item) => {
          return item[this.attrs.trackBy] === value || item[this.displayedBy] === value
        })
      } else {
        itemIndex = this.localFullObject.findIndex((item) => {
          return item[this.displayedBy] === value
        })
      }

      if (itemIndex === -1) {
        return
      }

      const updatedFullObject = [...this.localFullObject]
      updatedFullObject.splice(itemIndex, 1)

      const updatedModel = [...this.context.model]
      updatedModel.splice(itemIndex, 1)

      this.localFullObject = updatedFullObject
      this.context.model = updatedModel

      this.$emit('update:fullObject', updatedFullObject)
    },

    isItemDisabled(value) {
      const item = this.localFullObject.find((obj) => obj[this.displayedBy] === value)
      return item?.disableCheckbox || false
    },
  },
}
</script>

<style lang="scss" scoped>
@import './style.scss';
</style>
